// ABOUTME: Editable chart section for reports with DataEditorModal integration.
// ABOUTME: Displays charts in view mode with instant optimistic updates on edit.

import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, Trash2, Pencil, Table2 } from 'lucide-react';
import { ReportChart } from './ReportChart';
import { ReportTable, DEFAULT_MAX_DISPLAY_ROWS } from './ReportTable';
import { DataEditorModal } from './DataEditorModal';
import { TableEditorModal } from './TableEditorModal';
import { normalizeChartType } from './utils/chart-data';
import type { VisualizationConfig } from '@/types/api';
import type { DataPoint } from '@/types';
import { detectChartStructure, type ChartType } from '@/utils/chartValidation';

interface ChartSectionProps {
  reportId: string;
  sectionId: string;
  reportVersion: number;
  content?: string;
  visualization?: VisualizationConfig;
  tableData?: { headers: string[]; rows: (string | number)[][] } | null;
  maxDisplayRows?: number;
  onTableSave?: (data: { title: string; headers: string[]; rows: (string | number)[][] }) => Promise<{ version: number }>;
  onMaxDisplayRowsChange?: (maxRows: number) => Promise<void>;
  onUpdate: (content: string) => void;
  onDelete: () => void;
  onVersionUpdate?: (newVersion: number) => void;
  onRefetch?: () => void;
  isDragging?: boolean;
  dragHandleProps?: Record<string, unknown>;
}

// Parse content JSON to extract chart data when visualization prop is not available
function parseContentData(content?: string): { data: DataPoint[]; type: ChartType; title?: string } | null {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content);
    if (parsed && Array.isArray(parsed.data) && parsed.data.length > 0) {
      const structure = detectChartStructure(parsed.data);
      const labelKey = structure.categoryKey ?? 'label';
      const valueKey = structure.seriesKeys[0] ?? 'value';
      return {
        data: parsed.data.map((d: Record<string, unknown>) => ({
          label: String(d[labelKey] ?? d.label ?? d.name ?? ''),
          value: Number(d[valueKey] ?? d.value ?? 0),
          prev: d.prev as number | undefined,
          group: d.group as string | undefined,
        })),
        type: (parsed.type as ChartType) || 'bar',
        title: parsed.title,
      };
    }
  } catch {
    // Content is not valid JSON
  }
  return null;
}

export function ChartSection({
  reportId,
  sectionId,
  reportVersion,
  content,
  visualization,
  tableData,
  maxDisplayRows,
  onTableSave,
  onMaxDisplayRowsChange,
  onUpdate: _onUpdate,
  onDelete,
  onVersionUpdate,
  onRefetch,
  isDragging,
  dragHandleProps,
}: ChartSectionProps) {
  void _onUpdate;

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingTable, setIsEditingTable] = useState(false);
  const [localVersion, setLocalVersion] = useState(reportVersion);
  const [isToggling, setIsToggling] = useState(false);

  // Local state for optimistic table data updates after editing
  const [localTableData, setLocalTableData] = useState<{ headers: string[]; rows: (string | number)[][] } | null>(null);
  const effectiveTableData = localTableData || tableData;

  // Local state for optimistic updates (raw Record format for multi-series support)
  const [localRawData, setLocalRawData] = useState<Record<string, unknown>[] | null>(null);
  const [localChartType, setLocalChartType] = useState<ChartType | null>(null);

  // Parse content as fallback when visualization prop is not available
  const parsedContent = parseContentData(content);

  // Determine the effective raw data source for multi-series detection
  const effectiveRawData = localRawData || visualization?.data || null;

  // Check if effective data is multi-series by detecting structure
  const isMultiSeries = (() => {
    if (!effectiveRawData || effectiveRawData.length === 0) return false;
    const structure = detectChartStructure(effectiveRawData);
    return structure.seriesKeys.length > 1;
  })();

  // Get raw data for the DataEditorModal (always Record<string, unknown>[])
  const getEditorData = useCallback((): Record<string, unknown>[] => {
    if (localRawData) {
      return localRawData;
    }
    if (visualization?.data && visualization.data.length > 0) {
      return visualization.data;
    }
    // Fallback to parsed content converted to Record format
    if (parsedContent?.data) {
      return parsedContent.data.map(d => ({
        label: d.label,
        value: d.value,
        ...(d.prev !== undefined ? { prev: d.prev } : {}),
      }));
    }
    return [];
  }, [visualization?.data, localRawData, parsedContent]);

  const getChartData = useCallback((): DataPoint[] => {
    if (localRawData) {
      // Convert localRawData to DataPoint format for single-series rendering
      const structure = detectChartStructure(localRawData);
      const labelKey = structure.categoryKey ?? 'label';
      const valueKey = structure.seriesKeys[0] ?? 'value';
      return localRawData.map((d: Record<string, unknown>) => ({
        label: String(d[labelKey] ?? ''),
        value: Number(d[valueKey] ?? 0),
        prev: d.prev as number | undefined,
        group: d.group as string | undefined,
      }));
    }

    // Multi-series data is passed via rawData prop, not here
    if (isMultiSeries) {
      return [];
    }

    if (visualization?.data && visualization.data.length > 0) {
      const structure = detectChartStructure(visualization.data as Record<string, unknown>[]);
      const labelKey = structure.categoryKey ?? 'label';
      const valueKey = structure.seriesKeys[0] ?? 'value';
      return visualization.data.map((d: Record<string, unknown>) => ({
        label: String(d[labelKey] ?? d.label ?? d.name ?? ''),
        value: Number(d[valueKey] ?? d.value ?? 0),
        prev: d.prev as number | undefined,
        group: d.group as string | undefined
      }));
    }

    // Fallback to parsed content for newly added charts
    if (parsedContent?.data) {
      return parsedContent.data;
    }

    return [];
  }, [visualization?.data, localRawData, parsedContent, isMultiSeries]);

  const getChartType = useCallback((): ChartType => {
    if (localChartType) {
      return localChartType;
    }
    // chart_type at top level has priority over config.type
    if (visualization?.chart_type) {
      return visualization.chart_type as ChartType;
    }
    if (visualization?.config?.type) {
      return visualization.config.type as ChartType;
    }
    // Fallback to parsed content type
    return parsedContent?.type || 'bar';
  }, [visualization?.chart_type, visualization?.config?.type, localChartType, parsedContent?.type]);

  // Get title from visualization or parsed content
  const chartTitle = visualization?.title || parsedContent?.title;

  const chartData = getChartData();
  const chartType = getChartType();

  const getDisplayChartType = (): 'bar' | 'line' | 'area' | 'pie' => {
    return normalizeChartType(chartType);
  };

  const handleSaveSuccess = useCallback((newVersion: number, data: Record<string, unknown>[], newChartType: ChartType) => {
    setLocalRawData(data);
    setLocalChartType(newChartType);
    setLocalVersion(newVersion);

    if (onVersionUpdate) {
      onVersionUpdate(newVersion);
    }
    if (effectiveTableData) {
      onRefetch?.();
    }
  }, [onVersionUpdate, onRefetch, effectiveTableData]);

  // Handle table editor save — call backend first, then update local state on success
  const handleTableSaveSuccess = useCallback(async (data: { title: string; headers: string[]; rows: (string | number)[][] }) => {
    if (onTableSave) {
      const response = await onTableSave(data);
      setLocalTableData({ headers: data.headers, rows: data.rows });
      setLocalVersion(response.version);
    }
  }, [onTableSave]);

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`group relative ${isDragging ? 'opacity-50' : ''}`}
      >
        {/* Drag Handle */}
        <div className="absolute -left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            {...dragHandleProps}
            className="p-1 text-surface-400 hover:text-surface-600 cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </div>

        {/* Chart Content */}
        <div
          className="relative rounded-sm bg-surface-0 border border-surface-200 p-4 hover:border-surface-300 transition-colors cursor-pointer"
          onClick={() => setIsEditing(true)}
        >
          {/* Inline Edit/Delete Controls */}
          <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {effectiveTableData && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingTable(true);
                }}
                className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                title="Edit Table"
              >
                <Table2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-error hover:bg-error-light transition-colors cursor-pointer"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {chartTitle && (
            <div className="text-sm font-medium text-surface-600 mb-3">
              {chartTitle}
            </div>
          )}
          {/* Only render chart when there's actual chart data (table-only sections skip this) */}
          {(chartData.length > 0 || (effectiveRawData && effectiveRawData.length > 0)) && (
            <ReportChart
              data={isMultiSeries ? undefined : chartData}
              rawData={isMultiSeries ? (localRawData || visualization?.data) : undefined}
              type={getDisplayChartType()}
              height={250}
              showLegend={isMultiSeries}
            />
          )}
        </div>
      </motion.div>

      {/* Table data rendered below chart (for converted chart-table insights) */}
      {effectiveTableData && effectiveTableData.headers && effectiveTableData.rows && (
        <div className="mt-4 rounded-sm bg-surface-0 border border-surface-200 p-4">
          <ReportTable
            data={effectiveTableData}
            maxDisplayRows={maxDisplayRows}
            toggleDisabled={isToggling}
            onToggleExpand={onMaxDisplayRowsChange ? async (showAll) => {
              const totalRows = effectiveTableData.rows.length;
              setIsToggling(true);
              try {
                await onMaxDisplayRowsChange(showAll ? totalRows : DEFAULT_MAX_DISPLAY_ROWS);
              } finally {
                setIsToggling(false);
              }
            } : undefined}
          />
        </div>
      )}

      {/* Data Editor Panel */}
      <DataEditorModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        reportId={reportId}
        sectionId={sectionId}
        initialData={getEditorData()}
        initialChartType={chartType}
        initialTitle={chartTitle}
        initialCategoryKey={visualization?.category_key}
        version={localVersion}
        onSaveSuccess={handleSaveSuccess}
      />

      {/* Table Editor Modal (for converted chart-table sections) */}
      {effectiveTableData && (
        <TableEditorModal
          isOpen={isEditingTable}
          onClose={() => setIsEditingTable(false)}
          initialData={{ title: chartTitle || 'Table', headers: effectiveTableData.headers, rows: effectiveTableData.rows }}
          onSave={handleTableSaveSuccess}
        />
      )}
    </>
  );
}
